<template>
  <v-dialog v-model="dialog" scrollable width="600">
    <v-card>
      <v-card-title>
        {{ allowTranslationsEdit ? $t('translations_edit_languages') : $t('translations_change_reference_language') }}
      </v-card-title>
      <v-card-text>
        <div class="font-weight-bold mt-4">
          {{$t('translations_current_reference_language')}}
        </div>
        <div>
          {{$t('languages_' + defaultLanguage)}}
        </div>

        <div class="font-weight-bold mt-4">
          {{$t('translations_new_reference_language')}}
        </div>
        <div class="mb-3">
          {{$t('translations_reference_language_hint')}}
        </div>
        <v-autocomplete
          v-model="newDefaultLanguage"
          :items="availableDefaultLanguages"
          item-value="key"
          item-text="name"
          :label="$t('translations_select_reference_language')"
          :no-data-text="availableDefaultLanguages.length === 0 ? $t('translations_reference_language_empty_hint') : $t('no_match_found')"
          solo
          class="mt-2"
        >
        </v-autocomplete>

        <!-- Shown in Translations tab, hidden in Content Builder tab -->
        <div v-if="allowTranslationsEdit">
          <div class="font-weight-bold">
            {{$t('translations')}}
          </div>
          <v-autocomplete
            v-model="selectedTranslationLanguages"
            :items="allLanguagesDropdown"
            :search-input.sync="allLanguagesSearchInput"
            @change="allLanguagesSearchInput=''"
            item-text="name"
            item-value="key"
            chips
            :label="$t('select_languages')"
            placeholder="Search for a language"
            multiple
            class="my-3"
          >
            <template v-slot:selection="data">
              <v-chip
                v-bind="data.attrs"
                :input-value="data.selected"
                close
                @click="data.select"
                @click:close="removeSelectedTranslationLanguage(data.item)"
              >
                {{ data.item.name }}
              </v-chip>
            </template>
          </v-autocomplete>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-btn
          v-if="showGoToTranslations"
          @click="goToTranslations"
          color="primary"
          text
        >
          {{ $t('go_to_translations') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="closeDialog" text>{{$t('close')}}</v-btn>
        <v-btn @click="saveLanguagesEdit" raised color="primary" class="btn-text-white">
          {{$t('save')}}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import Languages from '@/utils/languages'
import translationUtils from '@/utils/translations'
import { removeHtmlTags } from '@/utils/configHelpers'

export default {
  name: 'LanguageEditModal',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    allowTranslationsEdit: {
      type: Boolean,
      default: false
    },
    showGoToTranslations: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters(['defaultLanguage', 'enabledLanguages', 'editor', 'published']),

    dialog: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },

    allLanguagesDropdown () {
      const collator = new Intl.Collator(this.$i18n.locale)
      const sorted = Languages.list.slice().sort((a, b) =>
        collator.compare(this.$t(`languages_${a.key}`), this.$t(`languages_${b.key}`))
      )
      const defaultLang = this.newDefaultLanguage || this.defaultLanguage
      return sorted.filter((lang) => lang.key !== defaultLang)
    },

    availableDefaultLanguages () {
      if (!this.dialog || !this.editor || !this.editor.enabled_languages) {
        return []
      }
      const { translationsStrings } = translationUtils.getTranslationsForLanguage(this.editor, this.defaultLanguage, null, true)
      const defaultLanguageHasContent = Object.values(translationsStrings).some((val) => this.isTranslated(val))

      // If the default language has no content, show all other enabled languages. If default language does have content, show only fully translated languages
      return Languages.list
        .filter((lang) => this.editor.enabled_languages.includes(lang.key) && lang.key !== this.defaultLanguage)
        .filter((lang) => !defaultLanguageHasContent || this.isLanguageFullyTranslated(lang.key))
        .map((lang) => ({ key: lang.key, name: this.$t(`languages_${lang.key}`) }))
    }
  },
  data () {
    return {
      newDefaultLanguage: '',
      originalDefaultLanguage: '',
      selectedTranslationLanguages: [],
      allLanguagesSearchInput: ''
    }
  },
  methods: {
    ...mapMutations({
      setDefaultLanguage: 'SET_DEFAULT_LANGUAGE'
    }),

    closeDialog () {
      this.dialog = false
      this.resetData()
    },

    goToTranslations () {
      this.closeDialog()
      this.$router.push({ name: 'config-global' })
    },

    saveLanguagesEdit () {
      const previousDefaultLanguage = this.defaultLanguage
      const newDefaultLanguage = this.newDefaultLanguage || this.defaultLanguage

      if (newDefaultLanguage !== previousDefaultLanguage) {
        this.setDefaultLanguage(newDefaultLanguage)
        this.$emit('default-language-changed', newDefaultLanguage)
      }

      if (this.allowTranslationsEdit) {
        const enabledLanguages = [...this.selectedTranslationLanguages]
        if (!enabledLanguages.includes(newDefaultLanguage)) {
          enabledLanguages.push(newDefaultLanguage)
        }

        const uniqueEnabledLanguages = [...new Set(enabledLanguages)]
        this.$emit('translation-languages-changed', uniqueEnabledLanguages)
      }

      this.closeDialog()
    },

    removeSelectedTranslationLanguage (lang) {
      this.selectedTranslationLanguages = this.selectedTranslationLanguages.filter((l) => l !== lang.key)
    },

    resetData () {
      this.newDefaultLanguage = ''
      this.originalDefaultLanguage = ''
      this.selectedTranslationLanguages = []
      this.allLanguagesSearchInput = ''
    },

    isTranslated (val) {
      if (val == null) {
        return false
      }
      if (typeof val === 'string') {
        const textContent = removeHtmlTags(val)
        return textContent.trim().length > 0
      }
      return true
    },

    isLanguageFullyTranslated (lang) {
      const { translationsStrings, translatedStrings } = translationUtils.getTranslationsForLanguage(this.editor, this.defaultLanguage, lang, true)
      const translationKeys = Object.keys(translationsStrings)

      if (!translationKeys.length) {
        return false
      }

      return translationKeys.every((key) => {
        const defaultVal = translationsStrings[key]
        if (!this.isTranslated(defaultVal)) {
          return true
        }
        return this.isTranslated(translatedStrings[key])
      })
    }
  },
  watch: {
    value (isDialogOpen) {
      if (isDialogOpen) {
        // Initialize data
        this.selectedTranslationLanguages = [...this.enabledLanguages].filter((lang) => lang !== this.defaultLanguage)
        this.originalDefaultLanguage = this.defaultLanguage
      }
    },

    // When reference language changes, remove the new reference language from translation list and add the previous reference language to the list
    newDefaultLanguage (newVal) {
      if (!this.dialog || !this.allowTranslationsEdit) {
        return
      }

      const originalDefaultLang = this.originalDefaultLanguage || this.defaultLanguage

      if (!newVal) {
        this.selectedTranslationLanguages = this.selectedTranslationLanguages.filter((lang) => lang !== originalDefaultLang)
        return
      }

      this.selectedTranslationLanguages = this.selectedTranslationLanguages.filter((lang) => lang !== newVal)

      if (originalDefaultLang && originalDefaultLang !== newVal && !this.selectedTranslationLanguages.includes(originalDefaultLang)) {
        this.selectedTranslationLanguages.push(originalDefaultLang)
      }
    }
  }
}
</script>

<style lang="sass" scoped>
.btn-text-white
  color: white !important
</style>
